import React from 'react';
import { StyleSheet, View } from 'react-native';
import { ODSText } from '../../atoms/ODSText';
import ODSButton from '../../atoms/ODSButton';
import { ODSIcon } from '../../atoms/ODSIcon';
import ODSBadges from '../../atoms/ODSBadges';
import { getTokens } from '../../../design-tokens/color-tokens';
import { getRentReviewOverallStatus } from '../../../utils/rentReviewUtils';

interface AvatarItem {
  text: string;
  bgColor?: string;
}

interface RentReviewCardProps {
  restStyle?: any;
  title?: string;
  description?: string;
  rentReviewData?: any;
  statusLabel?: string;
  avatars?: AvatarItem[];
  btnText?: string;
  btnTheme?: any;
  onClick?: () => void;
  onReviewRequest?: () => void;
}

const RentReviewCard: React.FC<RentReviewCardProps> = ({
  restStyle,
  title = 'Rent Review Request',
  description: descriptionProp,
  rentReviewData,
  statusLabel: statusLabelProp,
  avatars = [
    { text: 'S', bgColor: '#7A8B9E' },
    { text: 'S', bgColor: '#00A651' },
  ],
  btnText = 'Review Request',
  btnTheme,
  onClick,
  onReviewRequest,
}) => {
  const themeObj = getTokens('light').colors;

  const statusInfo = getRentReviewOverallStatus(rentReviewData);
  const statusLabel = statusLabelProp || statusInfo.statusLabel;
  const cardDescription = descriptionProp || statusInfo.description;

  const handlePress = () => {
    if (onReviewRequest) {
      onReviewRequest();
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <View style={[styles(themeObj).containerStyle, restStyle]}>
      {/* Header Row: Icon + Title + Status Badge */}
      <View style={styles(themeObj).headerRow}>
        <View style={styles(themeObj).headerLeft}>
          <View style={styles(themeObj).iconContainer}>
            <ODSIcon icon="TB-Alert" size={24} color="#1FA6B2" />
          </View>
          <ODSText type="h4" color={themeObj.text.primary} style={{ fontWeight: '700', flex: 1, flexShrink: 1 }}>
            {title}
          </ODSText>
        </View>
        <ODSBadges type={statusLabel}>{statusLabel}</ODSBadges>
      </View>

      {/* Description Row */}
      <View style={styles(themeObj).descRow}>
        <ODSText type="sm" color={themeObj.text.secondary} style={{ flexShrink: 1 }}>
          {cardDescription}
        </ODSText>
      </View>

      {/* Action Button */}
      <View style={styles(themeObj).buttonContainer}>
        <ODSButton
          type="primary"
          disabled={false}
          themeColor={btnTheme || themeObj.button?.main || themeObj.main.blue}
          onPress={handlePress}
        >
          {btnText}
        </ODSButton>
      </View>
    </View>
  );
};

const styles = (theme: any) =>
  StyleSheet.create({
    containerStyle: {
      width: '100%',
      borderRadius: 16,
      shadowColor: '#101828',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 4,
      elevation: 2,
      backgroundColor: theme.neutral?.white || '#FFFFFF',
      padding: 24,
      borderWidth: 1,
      borderColor: '#E4E7EC',
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 12,
    },
    headerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      marginRight: 8,
    },
    iconContainer: {
      marginRight: 10,
      justifyContent: 'center',
      alignItems: 'center',
    },
    descRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      marginBottom: 24,
    },
    buttonContainer: {
      width: '100%',
    },
  });

export default RentReviewCard;
